import React, { useState, useEffect } from "react";
import HelpButton from '../../tours/HelpButton';
import useAutoTour from '../../tours/useAutoTour';
import useChatIntro from '../../tours/useChatIntro';
import InvoicingDashboard from "../InvoicingDashboard";
import ExpenseList from "./ExpenseList";
import TaxSummary from "./TaxSummary";
import ProjectFinancials from "../Projects/ProjectFinancials";
import TransactionsList from "./TransactionsList";
import RecurringInvoices from "./RecurringInvoices";
const PurchaseOrders = React.lazy(() => import("../PurchaseOrders/PurchaseOrders"));

// ─── Sub-navigation items ──────────────────────────────────────────────────────
const NAV_ITEMS = [
  { id: "invoices",        label: "Invoices" },
  { id: "recurring",       label: "Recurring invoices" },
  { id: "purchase-orders", label: "Purchase Orders" },
  { id: "expenses",        label: "Expenses" },
  { id: "vat",             label: "Tax Summary" },
  { id: "transactions",    label: "Transactions" },
];

// ─── Layout ───────────────────────────────────────────────────────────────────
function InvoicingLayout({ externalOpenFormWithProjectId, onExternalFormOpened, externalBillTimeEntries, onBillTimeOpened }) {
  useAutoTour('invoicing');
  useChatIntro('invoicing');
  const [section, setSection] = useState("invoices");
  const [financialProjectId, setFinancialProjectId] = useState(null);
  const [openFormWithProjectId, setOpenFormWithProjectId] = useState(null);
  const [billTimeEntries, setBillTimeEntries] = useState(null);

  useEffect(() => {
    if (externalOpenFormWithProjectId != null) {
      setFinancialProjectId(null);
      setSection("invoices");
      setOpenFormWithProjectId(externalOpenFormWithProjectId);
      onExternalFormOpened?.();
    }
  }, [externalOpenFormWithProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (externalBillTimeEntries != null) {
      setFinancialProjectId(null);
      setSection("invoices");
      setBillTimeEntries(externalBillTimeEntries);
      onBillTimeOpened?.();
    }
  }, [externalBillTimeEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleOpenProjectFinancials(projectId) {
    setFinancialProjectId(projectId);
  }

  function handleNewInvoiceForProject(projectId) {
    setFinancialProjectId(null);
    setSection("invoices");
    setOpenFormWithProjectId(projectId);
  }

  function handleFinancialsBack() {
    setFinancialProjectId(null);
  }

  function renderContent() {
    if (financialProjectId != null) {
      return (
        <ProjectFinancials
          projectId={financialProjectId}
          onBack={handleFinancialsBack}
          onNewInvoice={handleNewInvoiceForProject}
        />
      );
    }
    switch (section) {
      case "invoices":        return <InvoicingDashboard onOpenProjectFinancials={handleOpenProjectFinancials} openFormWithProjectId={openFormWithProjectId} onFormOpened={() => setOpenFormWithProjectId(null)} billTimeEntries={billTimeEntries} onBillTimeFormOpened={() => setBillTimeEntries(null)} />;
      case "recurring":       return <RecurringInvoices />;
      case "purchase-orders": return <React.Suspense fallback={<div style={{ padding: '2rem', color: '#aaa' }}>Loading…</div>}><PurchaseOrders /></React.Suspense>;
      case "expenses":        return <ExpenseList />;
      case "vat":             return <TaxSummary />;
      case "transactions":    return <TransactionsList />;
      default:                return null;
    }
  }

  return (
    <div className="settings-layout">
      {/* Desktop: sticky left rail — reuses Settings CSS classes */}
      <nav data-tour-id="invoicing-nav" className="settings-nav-desktop" aria-label="Invoicing navigation">
        <div style={{
          padding: "1.75rem 1.5rem 0.5rem",
          display: 'flex',
          alignItems: 'center',
          fontFamily: "Poppins, sans-serif",
          fontSize: "0.68rem",
          fontWeight: 700,
          color: "#bbb",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          gap: 6,
        }}>
          Invoicing
          <HelpButton tourId="invoicing" />
        </div>
        {NAV_ITEMS.map(item => (
          <button
            key={item.id}
            data-tour-id={`invoicing-nav-${item.id}`}
            onClick={() => setSection(item.id)}
            className={`settings-nav-item${section === item.id ? " active" : ""}`}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {/* Mobile: select dropdown */}
      <div className="settings-nav-mobile">
        <label htmlFor="invoicing-section-select" className="settings-nav-select-label">
          Invoicing
        </label>
        <select
          id="invoicing-section-select"
          value={section}
          onChange={e => setSection(e.target.value)}
          className="settings-nav-select"
        >
          {NAV_ITEMS.map(item => (
            <option key={item.id} value={item.id}>{item.label}</option>
          ))}
        </select>
      </div>

      {/* Content pane */}
      <div className="settings-content">
        {renderContent()}
      </div>
    </div>
  );
}

export default InvoicingLayout;
